export class RBNode {
  static NIL = null;
  static initNIL() {
    RBNode.NIL = new RBNode(null, null);
    RBNode.NIL.parent = RBNode.NIL;
    RBNode.NIL.left = RBNode.NIL;
    RBNode.NIL.right = RBNode.NIL;
    RBNode.NIL.isBlack = true;
  }

  constructor(key, data = null) {
    this.key = key;
    this.data = data;

    this.parent = RBNode.NIL;
    this.left = RBNode.NIL;
    this.right = RBNode.NIL;

    this.isBlack = false;
  }

  setLeft(node) {
    node.setParent(this)
    if (this !== RBNode.NIL) this.left = node
    return this
  }
  setRight(node) {
    node.setParent(this)
    if (this !== RBNode.NIL) this.right = node
    return this
  }

  setParent(node) {
    if (this === RBNode.NIL) return
    this.parent = node
  }

  setIsBlack(b) {
    if (this === RBNode.NIL) return this
    if (b) this.isBlack = true
    else this.isBlack = false
    return this
  }

  getBlackHeight() {
    let h = 1; //garanteed black NIL node at leaf

    let node = this
    while (node !== RBNode.NIL) {
      if (node.isBlack) h++;
      node = node.left;
    }

    return h;
  }
}

RBNode.initNIL()

const defaultDataMergeFunc = (x1, _x2) => x1
const defaultDataSubtractFunc = (_a, _x) => null
export class RBTree {
  constructor(dataMergeFunc = defaultDataMergeFunc, dataSubtractFunc = defaultDataSubtractFunc) {
    this.root = RBNode.NIL;
    this.dataMergeFunc = dataMergeFunc
    this.dataSubtractFunc = dataSubtractFunc
  }

  clear() {
    this.root = RBNode.NIL
  }

  setRoot(node) {
    node.parent = RBNode.NIL
    this.root = node
  }

  rotate(right, sub) {
    const sub_parent = sub.parent;
    const new_root = right ? sub.left : sub.right
    const new_child = right ? new_root.right : new_root.left

    const isRightChild = sub === sub_parent.right

    if (right) {
      sub.setLeft(new_child);
      new_root.setRight(sub);
    } else {
      sub.setRight(new_child);
      new_root.setLeft(sub);
    }

    if (isRightChild) sub_parent.setRight(new_root)
    else sub_parent.setLeft(new_root)

    if (sub === this.root) this.root = new_root

    return new_root
  }

  find(key) {
    let node = this.root;
    while (node !== RBNode.NIL) {
      if (key === node.key) return node;
      node = key < node.key ? node.left : node.right;
    }

    return null;
  }

  insert(key, data) {
    let current = this.root;
    let previous = null;

    while (current !== RBNode.NIL && key != current.key) {
      previous = current
      if (key < current.key) current = current.left;
      else current = current.right;
    }

    if (current === RBNode.NIL) {
      const node = new RBNode(key, data)

      if (previous === null) this.setRoot(node);
      else if (node.key < previous.key) previous.setLeft(node);
      else previous.setRight(node);
      this.fixInsert(node);
      this.root.setIsBlack(true)
    } else {
      current.data = this.dataMergeFunc(current.data, data)
    }
  }

  fixInsert(node) {
    let parent = node.parent
    do {
      if (parent.isBlack) return;

      const grandparent = parent.parent;

      if (grandparent === RBNode.NIL) {
        parent.setIsBlack(true)
        return;
      }

      const isRightChild = node === parent.right
      const isParentRightChild = parent === grandparent.right
      const uncle = isParentRightChild ? grandparent.left : grandparent.right
      if (uncle.isBlack) {
        if (isRightChild != isParentRightChild) {
          this.rotate(isParentRightChild, parent)
          node = parent;
          parent = isParentRightChild ? grandparent.right : grandparent.left;
        }

        this.rotate(!isParentRightChild, grandparent)
        parent.setIsBlack(true);
        grandparent.setIsBlack(false);
        return;
      }

      parent.setIsBlack(true)
      uncle.setIsBlack(true)
      grandparent.setIsBlack(false)
      node = grandparent;

    } while ((parent = node.parent) !== RBNode.NIL);
  }

  removeKey(key) {
    let nodeToRemove = this.find(key)
    if (!nodeToRemove) return

    let replacementNode = nodeToRemove.left === RBNode.NIL
      ? nodeToRemove.right
      : (nodeToRemove.right === RBNode.NIL ? nodeToRemove.left : null)

    // if both children are not null
    if (!replacementNode) {
      // find the successor node
      replacementNode = nodeToRemove.right
      while (replacementNode.left !== RBNode.NIL) replacementNode = replacementNode.left

      // substitute the nodeToRemove for the successor
      nodeToRemove.key = replacementNode.key
      nodeToRemove.data = replacementNode.data

      // remove the successor
      nodeToRemove = replacementNode
      replacementNode = replacementNode.right
    }

    const replacementNodeParent = nodeToRemove.parent

    if (replacementNodeParent === RBNode.NIL) this.setRoot(replacementNode)
    else if (nodeToRemove === replacementNodeParent.left) replacementNodeParent.setLeft(replacementNode)
    else replacementNodeParent.setRight(replacementNode)

    if (!nodeToRemove.isBlack || !replacementNode.isBlack)
      replacementNode.setIsBlack(true)
    else
      this.fixRemove(replacementNodeParent, replacementNode)

    this.root.setIsBlack(true)
  }

  fixRemove(nodeParent, node) {
    if (nodeParent === RBNode.NIL) return
    const isRightChild = node === nodeParent.right

    const sibling = isRightChild ? nodeParent.left : nodeParent.right
    const closeNephew = isRightChild ? sibling.right : sibling.left
    const distantNephew = isRightChild ? sibling.left : sibling.right

    // red sibling
    if (!sibling.isBlack) {
      this.rotate(isRightChild, nodeParent)

      nodeParent.setIsBlack(false)
      sibling.setIsBlack(true)
      this.fixRemove(nodeParent, node)
      return
    }

    // black sibling, both black nephews 
    if (closeNephew.isBlack && distantNephew.isBlack) {
      sibling.setIsBlack(false)
      if (nodeParent.isBlack) this.fixRemove(nodeParent.parent, nodeParent)
      else nodeParent.setIsBlack(true)
      return
    }

    // outer nephew is red
    if (!distantNephew.isBlack) {
      this.rotate(isRightChild, nodeParent)
      sibling.setIsBlack(nodeParent.isBlack)
      nodeParent.setIsBlack(true)
      distantNephew.setIsBlack(true)
      return
    }

    // outer nephew is black
    this.rotate(!isRightChild, sibling)
    this.rotate(isRightChild, nodeParent)
    closeNephew.setIsBlack(nodeParent.isBlack)
    nodeParent.setIsBlack(true)
    sibling.setIsBlack(true)
  }

  subtractData(key, data) {
    const node = this.find(key)
    if (!node) return
    node.data = this.dataSubtractFunc(node.data, data)
  }

  // Utility: In-order traversal
  inorder(node = this.root, result = []) {
    if (node === RBNode.NIL) return result;

    this.inorder(node.left, result);
    result.push({
      key: node.key,
      data: node.data,
      isBlack: node.isBlack,
      left: node.left.key,
      right: node.right.key
    });
    this.inorder(node.right, result);

    return result;
  }

  split(node, key) {
    if (node === RBNode.NIL) {
      return { left: RBNode.NIL, found: null, right: RBNode.NIL };
    }

    if (key === node.key) {
      return {
        left: node.left,
        found: node,
        right: node.right,
      };
    }

    if (key < node.key) {
      const res = this.split(node.left, key);
      return {
        left: res.left,
        found: res.found,
        right: this.join(res.right, node.key, node.right, node.data),
      };
    } else {
      const res = this.split(node.right, key);
      return {
        left: this.join(node.left, node.key, res.left, node.data),
        found: res.found,
        right: res.right,
      };
    }
  }

  joinRight(left, key, right, data = null, lbh = null, rbh = null) {
    if (lbh == null) lbh = left.getBlackHeight()
    if (rbh == null) rbh = right.getBlackHeight()
    if (left.isBlack && lbh == rbh) {
      return new RBNode(key, data).setLeft(left).setRight(right)
    }

    if (left.isBlack) lbh--

    const t = this.joinRight(left.right, key, right, data, lbh, rbh)

    left.setRight(t)

    if (left.isBlack && !t.isBlack && !t.right.isBlack) {
      t.right.setIsBlack(true);
      return this.rotate(false, left)
    }
    return left
  }

  joinLeft(left, key, right, data = null, lbh = null, rbh = null) {
    if (lbh == null) lbh = left.getBlackHeight()
    if (rbh == null) rbh = right.getBlackHeight()
    if (right.isBlack && lbh == rbh) {
      return new RBNode(key, data).setLeft(left).setRight(right)
    }

    if (right.isBlack) rbh--

    const t = this.joinLeft(left, key, right.left, data, lbh, rbh)

    right.setLeft(t)

    if (right.isBlack && !t.isBlack && !t.left.isBlack) {
      t.left.setIsBlack(true);
      return this.rotate(true, right)
    }
    return right
  }

  join(left, key, right, data = null) {
    const lbh = left.getBlackHeight()
    const rbh = right.getBlackHeight()

    if (lbh > rbh) {
      const t = this.joinRight(left, key, right, data, lbh, rbh)
      if (!t.isBlack && !t.right.isBlack) t.setIsBlack(true)
      return t
    }

    if (lbh < rbh) {
      const t = this.joinLeft(left, key, right, data, lbh, rbh)
      if (!t.isBlack && !t.left.isBlack) t.setIsBlack(true)
      return t
    }

    if (left.isBlack && right.isBlack) {
      return new RBNode(key, data).setLeft(left).setRight(right)
    }

    return new RBNode(key, data).setLeft(left).setRight(right).setIsBlack(true)
  }

  _union(t1root, t2root, dataMergeFunc = this.dataMergeFunc) {
    if (t1root === RBNode.NIL) return t2root;
    if (t2root === RBNode.NIL) return t1root;

    const { left: L1, right: R1, found } = this.split(t1root, t2root.key);
    const TL = this._union(L1, t2root.left, dataMergeFunc);
    const TR = this._union(R1, t2root.right, dataMergeFunc);

    const data = found ? dataMergeFunc(found.data, t2root.data) : t2root.data
    return this.join(TL, t2root.key, TR, data);
  }

  union(t2root, dataMergeFunc = this.dataMergeFunc) {
    const new_root = this._union(this.root, t2root, dataMergeFunc)
    this.setRoot(new_root)
    this.root.setIsBlack(true)
  }

  * rangeQuery(low, high, node = this.root) {
    if (node === RBNode.NIL) return;

    if (node.key >= low) {
      yield* this.rangeQuery(low, high, node.left);
    }

    if (node.key >= low && node.key < high) {
      yield node.data;
    }

    if (node.key < high) {
      yield* this.rangeQuery(low, high, node.right);
    }
  }
}

